import React, { useState, useEffect, useRef, useCallback } from 'react'; // <--- Le guillemet est corrigé ici
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Modal } from 'react-bootstrap';
import { ArrowLeft, Lock, Unlock, Maximize } from 'lucide-react'; 
import Navigation from '../components/Navbar';
import api from '../api';

const Watch = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [movie, setMovie] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const videoRef = useRef(null);
    const playerContainerRef = useRef(null); 
    const [hasJumped, setHasJumped] = useState(false);
    const [showResumeModal, setShowResumeModal] = useState(false);
    
    const [isLocked, setIsLocked] = useState(false); 
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const hideTimeoutRef = useRef(null);

    const resetTimer = useCallback(() => {
        setShowControls(true);
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => {
            setShowControls(false);
        }, 5000); 
    }, []);

    useEffect(() => {
        resetTimer();
        return () => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); };
    }, [resetTimer]);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            playerContainerRef.current.requestFullscreen().catch(err => {
                console.error(`Erreur plein écran: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
            if (!document.fullscreenElement) {
                setIsLocked(false);
                setShowControls(true);
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    useEffect(() => {
        const fetchMovie = async () => {
            try {
                const res = await api.get(`/movies/${id}`);
                setMovie(res.data);
                
                const token = localStorage.getItem('token');
                const filename = res.data.file_path;
                
                // On utilise la route de streaming sécurisée
                const secureUrl = `${api.defaults.baseURL}/video/stream/${encodeURIComponent(filename)}?token=${token}`;
                
                setVideoUrl(secureUrl);
            } catch (err) { 
                console.error(err); 
            }
        };
        fetchMovie();
    }, [id]);

    const savePosition = useCallback(async (time) => {
        if (!time || time <= 0) return;
        try { await api.post(`/movies/${id}/position`, { position: Math.floor(time) }); } catch (err) {}
    }, [id]);

    useEffect(() => {
        const v = videoRef.current;
        return () => { if (v) savePosition(v.currentTime); };
    }, [savePosition]);

    const handleTimeUpdate = () => {
        if (videoRef.current && !videoRef.current.paused) {
            if (Math.round(videoRef.current.currentTime) % 10 === 0) savePosition(videoRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (movie && movie.last_position > 10 && !hasJumped) setShowResumeModal(true);
    };

    const handleResume = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = movie.last_position;
            setHasJumped(true);
            setShowResumeModal(false);
            videoRef.current.play();
        }
    };

    const handleRestart = () => {
        setHasJumped(true);
        setShowResumeModal(false);
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
        }
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "00:00:00";
        const date = new Date(0);
        date.setSeconds(seconds);
        return date.toISOString().substr(11, 8);
    };

    return (
        <div className="min-vh-100 bg-black text-white">
            {!isLocked && <Navigation />}
            <Container fluid className="mt-5 pt-4 px-2 px-md-4">
                {!isLocked && (
                    <Button variant="link" className="text-white mb-3 p-0 d-flex align-items-center gap-2 text-decoration-none opacity-75" onClick={() => navigate('/')}>
                        <ArrowLeft size={20} /> Retour
                    </Button>
                )}

                <div className="d-flex justify-content-center">
                    <div 
                        ref={playerContainerRef}
                        onClick={resetTimer}
                        onMouseMove={resetTimer}
                        style={{ 
                            width: '100%', maxWidth: '1250px', backgroundColor: '#000', 
                            borderRadius: isFullscreen ? '0' : '12px', overflow: 'hidden', 
                            border: isFullscreen ? 'none' : '1px solid #222', position: 'relative',
                            cursor: (!showControls) ? 'none' : 'default'
                        }}
                    >
                        {/* BOUTON DÉVERROUILLER */}
                        {isLocked && isFullscreen && showControls && (
                            <Button 
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setIsLocked(false); 
                                    setShowControls(true); 
                                }} 
                                className="d-flex flex-column align-items-center justify-content-center border-2 border-white shadow-lg" 
                                style={{ 
                                    position: 'absolute', top: '30px', right: '30px', zIndex: 9999,
                                    width: '65px', height: '65px', borderRadius: '50%', backgroundColor: '#e50914' 
                                }}
                            >
                                <Lock size={24} />
                                <span className="fw-bold mt-1" style={{ fontSize: '8px' }}>DÉBLOQUER</span>
                            </Button>
                        )}

                        {/* BOUTON VERROUILLER */}
                        {isFullscreen && !isLocked && showControls && (
                            <Button
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setIsLocked(true); 
                                    resetTimer(); 
                                }}
                                style={{
                                    position: 'absolute', top: '30px', right: '30px', zIndex: 1000,
                                    backgroundColor: '#e50914', border: '2px solid white',
                                    borderRadius: '50%', width: '55px', height: '55px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <Unlock size={24} color="white" />
                            </Button>
                        )}

                        {/* BOUTON PLEIN ÉCRAN */}
                        {!isFullscreen && videoUrl && (
                            <Button
                                onClick={toggleFullscreen}
                                style={{
                                    position: 'absolute', top: '20px', right: '20px', zIndex: 1000,
                                    backgroundColor: '#e50914', border: '1px solid white',
                                    borderRadius: '50%', width: '45px', height: '45px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <Maximize size={20} color="white" />
                            </Button>
                        )}

                        {videoUrl && (
                            <video 
                                key={videoUrl} 
                                ref={videoRef} 
                                controls={!isLocked && showControls} 
                                playsInline
                                style={{ width: '100%', height: 'auto', maxHeight: isFullscreen ? '100vh' : '85vh', display: 'block' }}
                                onTimeUpdate={handleTimeUpdate} 
                                onLoadedMetadata={handleLoadedMetadata}
                            >
                                <source src={videoUrl} type="video/mp4" />
                            </video>
                        )}
                    </div>
                </div>

                {!isLocked && movie && (
                    <div className="mt-4 text-center pb-5">
                        <h2 className="fw-bold">{movie.title}</h2>
                        <p className="text-muted">{movie.genre} • {movie.release_year}</p>
                    </div>
                )}
            </Container>

            <Modal show={showResumeModal && !isLocked} onHide={handleRestart} centered contentClassName="bg-dark text-white border-secondary">
                <Modal.Body className="text-center p-4">
                    <h4 className="mb-4 fw-bold">Reprendre la lecture ?</h4>
                    <p className="text-muted small">Dernière position : {formatTime(movie?.last_position)}</p>
                    <div className="d-flex justify-content-center gap-3 mt-3">
                        <Button variant="danger" onClick={handleResume}>Reprendre</Button>
                        <Button variant="outline-light" onClick={handleRestart}>Recommencer</Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Watch;